import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import { Store } from "./storage";
import AppStore from "./appStore";
import { postQuery } from "../utils/api";
import {
  getParksQuery,
  getLocationDetailsQuery,
  getAmenitiesQuery,
  getLocationGoogleReviewQuery,
} from "../utils/queries";
import { getDistancesOfLocationsFromUser } from "../utils/geolocation";
import STRINGS from "../constants/strings";

//Custom storage
const storage = Store("session");

//State
const State = {
  pageText: "",
  parks: [],
  mainParksList: [],
  amenities: [],
  amenitiesText: "All Amenities",
  selectedAmenities: [],
  errorMessage: "",
  satelliteView: false,
  locationDetails: null,
  googleReviews: null,
  sortBy: null,
  searchText: "",
};

//Actions
const Actions = (_set, get) => {
  const set = _set;
  const appStore = AppStore.getState();

  /**
   * Sets the page text in the store.
   * @param {string} name - The text to set for the page.
   */
  const setPageText = (name) => {
    set({ pageText: name });
  };

  /**
   * Asynchronously fetches park facilities data from the API and updates the store.
   * Sets loading and error states in the app store during the process.
   * On success, updates the parks state with the fetched facilities.
   * On failure, logs the error and sets an error message.
   *
   * @async
   * @function getParks
   * @returns {Promise<void>} Resolves when the parks data has been fetched and the store updated.
   */
  const getParks = async () => {
    let parks = [];
    let errorMessage = STRINGS.errorUnableToLoadParks;

    appStore.setLoading(true);

    try {
      const response = await postQuery(process.env.REACT_APP_API_URL, getParksQuery);
      if (response.statusCode === 200) {
        const dataParks = response.data.getParks.facilities;
        if (dataParks && dataParks.length > 0) {
          parks = dataParks;
          errorMessage = "";
        } else {
          errorMessage = STRINGS.noParksDataFound;
        }
      }
    } catch (error) {
      console.error("Error fetching parks:", error);
    }
    set({ mainParksList: parks });
    filterParks();
    sortParks(get().sortBy || "name-ascending");
    appStore.setErrorMessage(errorMessage);
    appStore.setLoading(false);
  };

  /**
   * Fetches detailed information about a specific location by its name.
   * Sets loading and error states in the app store during the process.
   *
   * @async
   * @function getLocationDetails
   * @param {string} locationName - The name of the location to fetch details for.
   * @returns {Promise<void>} Resolves when the location details have been fetched and state updated.
   */
  const getLocationDetails = async (locationName) => {
    let locationDetails = null;
    let errorMessage = STRINGS.errorUnableToLoadParkDetails;

    appStore.setLoading(true);

    try {
      const response = await postQuery(process.env.REACT_APP_API_URL, getLocationDetailsQuery, { locationName });
      if (response.statusCode === 200) {
        locationDetails = response.data.getLocationDetails.item;
        getLocationGoogleReview(locationDetails.googlePlacesId);
        errorMessage = "";
      }
    } catch (error) {
      console.error("Error fetching location details:", error);
    }

    set({ locationDetails });
    appStore.setErrorMessage(errorMessage);
    appStore.setLoading(false);
  };

  /**
   * Toggles the satellite view state in the store.
   */
  const toggleSatelliteView = () => {
    set({ satelliteView: !get().satelliteView });
  };

  /**
   * Fetches amenities from the API and updates the store.
   * Adds a selected property to each amenity.
   * @returns {Promise<void>} Resolves when amenities have been fetched and updated in the store.
   */
  const getAmenities = async () => {
    let amenities = [];
    let errorMessage = STRINGS.errorUnableToLoadAmenities;

    try {
      const response = await postQuery(process.env.REACT_APP_API_URL, getAmenitiesQuery);
      if (response.statusCode === 200) {
        amenities = response.data.getParks.amenities;
        if (amenities && amenities.length > 0) {
          const selectedAmenities = get().selectedAmenities || [];
          // Add selected property to each amenity
          amenities = amenities.map((obj) => ({ ...obj, selected: selectedAmenities.includes(obj.ID) }));
          errorMessage = "";
        } else {
          errorMessage = STRINGS.noAmenitiesDataFound;
        }
      }
    } catch (error) {
      console.error("Error fetching amenities:", error);
    }
    set({ amenities, errorMessage });
    appStore.setErrorMessage(errorMessage);
  };

  /**
   * Toggles the selected state of a specific amenity.
   * @param {string|number} amenityId - The ID of the amenity to toggle.
   */
  const toggleAmenity = (amenityId) => {
    const amenities = get().amenities;
    const amenity = amenities.find((a) => a.ID === amenityId);
    if (amenity) {
      const updatedAmenities = amenities.map((a) => (a.ID === amenityId ? { ...a, selected: !a.selected } : a));
      set({ amenities: updatedAmenities });
    }
  };

  /**
   * Updates the selected amenities list and filters parks accordingly.
   */
  const updateSelectedAmenities = () => {
    const amenities = get().amenities;
    const selectedAmenities = amenities.filter((a) => a.selected).map((a) => a.ID);
    set({ selectedAmenities });
     setFilteredParks();
  };

  /**
   * Resets the overrideVerify state in the store.
   * @param {boolean} state - The state to set for overrideVerify.
   */
  const resetVerify = (state) => {
    set({ overrideVerify: state });
  };

  /**
   * Resets all amenities to unselected and restores the parks list.
   */
  const resetAmenities = () => {
    const amenities = get().amenities;
    const resetAmenitiesList = amenities.map((a) => ({ ...a, selected: false }));
    set({ amenities: resetAmenitiesList, selectedAmenities: [], parks: get().mainParksList });
    setFilteredParks();
  };
  /**
   * Filters parks based on selected amenities.
   */
  const filterParks = (parks = get().mainParksList) => {
    const selectedAmenities = get().selectedAmenities;
    if (selectedAmenities.length > 0) {
      const filtered = parks.filter((park) => {
        let matched = false;
        if (park.amenities?.length > 0) {
          matched = park.amenities.filter((amenity) => selectedAmenities.includes(amenity)).length > 0;
        }
        return matched;
      });
      set({ parks: filtered });
    } else {
      set({ parks });
    }
  };

  /**
   * Selects all amenities and filters parks accordingly.
   */
  const selectAllAmenities = () => {
    const amenities = get().amenities;
    const allAmenitiesList = amenities.map((a) => ({ ...a, selected: true }));
    const selectedAmenities = amenities.map((a) => a.ID);
    set({ amenities: allAmenitiesList, selectedAmenities });
    setFilteredParks();
  };

  /**
   * Fetches Google reviews for a location and updates the store.
   * @param {string} placeId - The Google Places ID for the location.
   * @returns {Promise<void>} Resolves when Google reviews have been fetched and updated in the store.
   */
  const getLocationGoogleReview = async (placeId) => {
    let googleReviews = null;

    try {
      const response = await postQuery(process.env.REACT_APP_API_URL, getLocationGoogleReviewQuery, { placeId });
      if (response.statusCode === 200) {
        googleReviews = response.data.getLocationGoogleReview.placeDetails;
      }
    } catch (error) {
      console.log("Error fetching Google reviews:", error);
    }

    set({ googleReviews });
  };

  /**
   * Resets location details and Google reviews in the store.
   */
  const resetLocationDetails = () => {
    set({ locationDetails: null, googleReviews: null });
  };

  /**
   * Sorts parks based on the specified criteria (name/distance).
   * @param {string} by - The sorting criteria (e.g., 'name-ascending').
   */
  const sortParks = (by) => {
    const parks = get().parks;
    let sortedParks = [];
    if (parks && parks?.length > 0) {
      switch (by) {
        case "name-descending":
          sortedParks = [...parks].sort((a, b) => b.title.localeCompare(a.title));
          break;
        case "distance-ascending":
          sortedParks = [...parks].sort((a, b) => (a.distance || 0) - (b.distance || 0));
          break;
        case "distance-descending":
          sortedParks = [...parks].sort((a, b) => (b.distance || 0) - (a.distance || 0));
          break;
        case "name-ascending":
          sortedParks = [...parks].sort((a, b) => a.title.localeCompare(b.title));
          break;
        default:
          by = "name-ascending";
          sortedParks = [...parks].sort((a, b) => a.title.localeCompare(b.title));
      }
      set({ sortBy: by });
      set({ parks: sortedParks });
    }
  };

  /**
   * Asynchronously updates the distances of parks from the user's location.
   * - Fetches the list of parks from the store.
   * - Calculates distances using `getDistancesOfLocationsFromUser`.
   * - Updates both `mainParksList` and `parks` in the store with the new distances.
   * - Handles errors during the distance fetching process.
   *
   * @async
   * @function setDistance
   * @returns {Promise<void>} Resolves when the distances have been updated.
   */
  const setDistance = async () => {
    const parksList = get().mainParksList;
    const parks = get().parks;
    if (parksList.length >= 1) {
      try {
        const parksWithDistances = await getDistancesOfLocationsFromUser(parksList);
        if (parksWithDistances) {
          set({ mainParksList: parksWithDistances });

          const parksMap = new Map(parksWithDistances.map((parkItem) => [parkItem.ID, parkItem]));
          const updatedParks = parks.map((park) => (parksMap.has(park.ID) ? parksMap.get(park.ID) : park));
          set({ parks: updatedParks });
        }
      } catch (error) {
        console.error("Error fetching distances:", error);
      }
    }
  };

  /**
   * Updates the amenities text based on selected amenities.
   */
  const setAmenitiesText = () => {
    const amenitiesList = get().amenities;
    const selectedAmenities = amenitiesList.filter((a) => a.selected).map((a) => a.title);
    let amenitiesText = "All Amenities";
    switch (selectedAmenities.length) {
      case 0:
        amenitiesText = "All Amenities";
        break;
      case 1:
        amenitiesText = selectedAmenities[0];
        break;
      default:
        amenitiesText = `${selectedAmenities[0]} + ${selectedAmenities.length - 1} more`;
        break;
    }
    set({ amenitiesText });
  };

  const filterBySearchText = (text = get().searchText, parks = get().mainParksList, resetList = true) => {
    if (text && text.length > 0) {
      const searched = parks.filter((park) => {
        return park.title.toLowerCase().includes(text.toLowerCase());
      });
      set({ parks: searched });
    } else if (resetList) {
      set({ parks: get().mainParksList });
    }
  };

  /**
   * Sets the search text and triggers filtering and sorting.
   * @param {string} text - The search text to set.
   */
  const setSearchText = (text) => {
    set({ searchText: text });
    filterBySearchText(text, get().mainParksList);
    filterParks(get().parks);
    sortParks(get().sortBy || "name-ascending");
  };

  /**
   * Clears the search text and resets the filtered parks.
   */
  const clearSearchText = () => {
    set({ searchText: "" });
    filterParks();
    filterBySearchText(get().searchText, get().parks, false);
  };

  /**
   * Sets the filtered parks based on the current search text and sorting.
   */
  const setFilteredParks = () => {
    filterParks();
    filterBySearchText(get().searchText, get().parks, false);
    sortParks(get().sortBy || "name-ascending");
  };

  return {
    setPageText,
    getParks,
    getAmenities,
    resetVerify,
    toggleAmenity,
    updateSelectedAmenities,
    resetAmenities,
    filterParks,
    getLocationDetails,
    getLocationGoogleReview,
    resetLocationDetails,
    toggleSatelliteView,
    selectAllAmenities,
    setSearchText,
    clearSearchText,
    sortParks,
    setDistance,
    setAmenitiesText,
  };
};
//Store
export default create(
  persist(
    (set, get) => {
      const actions = Actions(set, get);
      return {
        ...State,
        setPageText: actions.setPageText,
        getParks: actions.getParks,
        getAmenities: actions.getAmenities,
        resetVerify: actions.resetVerify,
        toggleAmenity: actions.toggleAmenity,
        updateSelectedAmenities: actions.updateSelectedAmenities,
        resetAmenities: actions.resetAmenities,
        filterParks: actions.filterParks,
        getLocationDetails: actions.getLocationDetails,
        getLocationGoogleReview: actions.getLocationGoogleReview,
        resetLocationDetails: actions.resetLocationDetails,
        toggleSatelliteView: actions.toggleSatelliteView,
        selectAllAmenities: actions.selectAllAmenities,
        setSearchText: actions.setSearchText,
        clearSearchText: actions.clearSearchText,
        sortParks: actions.sortParks,
        setDistance: actions.setDistance,
        setAmenitiesText: actions.setAmenitiesText,
      };
    },
    {
      name: "app.parks",
      storage: createJSONStorage(() => storage),
    }
  )
);
